import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAdminSiteConfig } from '../hooks/useAdminSiteConfig';
import { resolvePublicMediaUrl } from '../utils/newsContent';
import type { AdminSettingsSection } from '../admin/adminSiteConfigData';
import { SETTINGS_SECTIONS } from '../admin/adminSiteConfigData';
import { GuildRankingCard, type GuildCardItem } from '../components/GuildRankingCard';
import type { GuildRankElementKey } from '../utils/guildRankConfig';
import type { GuildRankEffectKey } from '../types';
import '../styles/AdminNews.css';
import '../styles/AdminSiteConfig.css';
import '../styles/AdminLayout.css';
import '../styles/AdminGuildRank.css';
import '../styles/Rankings.css';
import AdminHomeCountdown from '../components/admin/AdminHomeCountdown';

const VALID_SECTIONS: AdminSettingsSection[] = ['general', 'links', 'navbar', 'home', 'download', 'theme', 'guildRank'];

const GUILDRANK_ELEMENTS: { key: GuildRankElementKey; label: string; sizeLabel: string; sizeMin: number; sizeMax: number; hasColor?: boolean }[] = [
    { key: 'emblem', label: 'Ícone da guild', sizeLabel: 'Tamanho (% do card)', sizeMin: 12, sizeMax: 70 },
    { key: 'placa', label: 'Placa do nome', sizeLabel: 'Largura (% do card)', sizeMin: 22, sizeMax: 95 },
    { key: 'rankBadge', label: 'Número do rank', sizeLabel: 'Tamanho da fonte (px)', sizeMin: 10, sizeMax: 34 },
    { key: 'name', label: 'Nome da guild', sizeLabel: 'Tamanho da fonte (px)', sizeMin: 10, sizeMax: 36, hasColor: true },
    { key: 'grade', label: 'Grade', sizeLabel: 'Tamanho da fonte (px)', sizeMin: 10, sizeMax: 34, hasColor: true },
    { key: 'master', label: 'Master', sizeLabel: 'Tamanho da fonte (px)', sizeMin: 10, sizeMax: 34, hasColor: true },
    { key: 'exploits', label: 'Exploits', sizeLabel: 'Tamanho da fonte (px)', sizeMin: 10, sizeMax: 34, hasColor: true },
];

const GUILDRANK_EFFECTS: { key: GuildRankEffectKey; label: string }[] = [
    { key: 'enabled', label: 'Ativar efeitos do 1º lugar' },
    { key: 'rays', label: 'Raios dourados girando' },
    { key: 'glow', label: 'Aura de brilho pulsante' },
    { key: 'crown', label: 'Coroa flutuante' },
    { key: 'sparkles', label: 'Faíscas brilhando' },
];

const SAMPLE_GUILD: GuildCardItem = {
    rank: 1,
    name: 'CR0WS',
    guildMarkId: 'basic00009',
    grade: 14,
    masterName: 'Master',
    exploits: 90708,
};

const AdminSiteConfig: React.FC = () => {
    const { section } = useParams<{ section?: string }>();
    const activeSection = (section as AdminSettingsSection) || 'general';

    const {
        config,
        saving,
        message,
        selectedThemeKey,
        setSelectedThemeKey,
        features,
        shortcuts,
        savedThemes,
        THEME_FIELDS,
        COLOR_PALETTE,
        updateLinks,
        updateHome,
        updateFeature,
        updateShortcut,
        updateTheme,
        updateGeneral,
        updateDownload,
        updateLogo,
        updateLogoNumber,
        updateNavbarPosition,
        updateMenuItem,
        addMenuItem,
        removeMenuItem,
        setSavedThemeSlot,
        saveCurrentThemeToSlot,
        applySavedTheme,
        handleSave,
        handleRestoreTheme,
        handleRestoreAll,
        guildRank,
        updateGuildRankTopLevel,
        updateGuildRankElement,
        updateGuildRankEffects,
        handleRestoreGuildRank,
    } = useAdminSiteConfig();

    if (!section) {
        return <Navigate to="/admin/settings/general" replace />;
    }

    if (!VALID_SECTIONS.includes(activeSection)) {
        return <Navigate to="/admin/settings/general" replace />;
    }

    const sectionMeta = SETTINGS_SECTIONS.find((s) => s.id === activeSection);

    return (
        <div className="admin-page admin-panel-content">
                <div className="admin-list-header" style={{ marginBottom: 24 }}>
                    <div>
                    <h1 className="admin-page-title">{sectionMeta?.label}</h1>
                    <p className="admin-page-subtitle">{sectionMeta?.description}</p>
                    </div>
                    <div className="admin-header-actions">
                    {activeSection === 'theme' && (
                        <button type="button" className="btn btn-secondary" onClick={handleRestoreTheme}>
                            Restaurar tema
                        </button>
                    )}
                    {activeSection === 'guildRank' && (
                        <button type="button" className="btn btn-secondary" onClick={handleRestoreGuildRank}>
                            Restaurar layout
                        </button>
                    )}
                    <button type="button" className="btn btn-secondary" onClick={handleRestoreAll}>
                        Restaurar tudo
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>

                {message && (
                    <div className={message.includes('sucesso') ? 'admin-success' : 'admin-error'} style={{ marginBottom: 16 }}>
                        {message}
                    </div>
                )}

            {activeSection === 'general' && (
                <div className="admin-form-wrapper">
                    <div className="form-group">
                        <label>Nome do site</label>
                        <input
                            type="text"
                            value={config.general?.siteName || ''}
                            onChange={e => updateGeneral('siteName', e.target.value)}
                            placeholder="Knight Icarus Online"
                        />
                    </div>
                </div>
            )}

            {activeSection === 'links' && (
                <div className="admin-form-wrapper">
                    <div className="form-group">
                        <label>Discord (convite)</label>
                        <input
                            type="url"
                            value={config.links?.discord || ''}
                            onChange={e => updateLinks('discord', e.target.value)}
                            placeholder="https://discord.gg/..."
                        />
                    </div>
                    <div className="form-group">
                        <label>Suporte / Create Ticket (Discord)</label>
                        <input
                            type="url"
                            value={config.links?.supportTicket || ''}
                            onChange={e => updateLinks('supportTicket', e.target.value)}
                            placeholder="https://discord.com/channels/..."
                        />
                    </div>
                </div>
            )}

            {activeSection === 'navbar' && (
                <div className="admin-form-wrapper">
                    <div className="form-group">
                        <label>Logo (URL/caminho)</label>
                        <input
                            type="text"
                            value={config.navbar?.logo?.src || ''}
                            onChange={e => updateLogo('src', e.target.value)}
                            placeholder="/assets/images/logo.png ou https://..."
                        />
                    </div>
                    <div className="form-group">
                        <label>Logo ALT (acessibilidade/SEO)</label>
                        <input
                            type="text"
                            value={config.navbar?.logo?.alt || ''}
                            onChange={e => updateLogo('alt', e.target.value)}
                            placeholder="Knight Icarus Online"
                        />
                    </div>
                    <div className="form-group">
                        <label>Altura da logo (px)</label>
                        <input
                            type="number"
                            min={16}
                            max={200}
                            value={config.navbar?.logo?.heightPx ?? 40}
                            onChange={e => updateLogoNumber('heightPx', Number(e.target.value || 40))}
                        />
                    </div>
                    <div className="form-group">
                        <label>Posição da logo</label>
                        <select
                            value={config.navbar?.logoPosition ?? 'left'}
                            onChange={e => updateNavbarPosition(e.target.value as 'left' | 'center')}
                        >
                            <option value="left">Esquerda</option>
                            <option value="center">Centro</option>
                        </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
                        <strong>Itens do menu</strong>
                        <button type="button" className="btn btn-secondary" onClick={addMenuItem}>
                            + Adicionar botão
                        </button>
                    </div>

                    <div style={{ marginTop: 12 }}>
                        {(config.navbar?.menuItems || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((item) => (
                            <div key={item.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--border-light)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                    <strong>{item.id}</strong>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            if (window.confirm(`Remover o item "${item.id}" do menu?`)) removeMenuItem(item.id);
                                        }}
                                    >
                                        Remover
                                    </button>
                                </div>

                                <div className="form-group" style={{ marginTop: 10 }}>
                                    <label>Visível</label>
                                    <select
                                        value={item.visible === false ? 'no' : 'yes'}
                                        onChange={e => updateMenuItem(item.id, { visible: e.target.value === 'yes' })}
                                    >
                                        <option value="yes">Sim</option>
                                        <option value="no">Não</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Tipo</label>
                                    <select
                                        value={item.type}
                                        onChange={e => updateMenuItem(item.id, { type: e.target.value as 'internal' | 'external' })}
                                    >
                                        <option value="internal">Interno (rota)</option>
                                        <option value="external">Externo (URL)</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Link (href)</label>
                                    <input
                                        type="text"
                                        value={item.href}
                                        onChange={e => updateMenuItem(item.id, { href: e.target.value })}
                                        placeholder={item.type === 'internal' ? '/forum' : 'https://forum...'}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Texto (EN)</label>
                                    <input
                                        type="text"
                                        value={item.label?.en || ''}
                                        onChange={e => updateMenuItem(item.id, { label: { ...item.label, en: e.target.value } })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Texto (PT-BR)</label>
                                    <input
                                        type="text"
                                        value={item.label?.['pt-BR'] || ''}
                                        onChange={e => updateMenuItem(item.id, { label: { ...item.label, 'pt-BR': e.target.value } })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Abrir em nova aba</label>
                                    <select
                                        value={item.newTab === false ? 'no' : 'yes'}
                                        onChange={e => updateMenuItem(item.id, { newTab: e.target.value === 'yes' })}
                                    >
                                        <option value="yes">Sim</option>
                                        <option value="no">Não</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Ordem</label>
                                    <input
                                        type="number"
                                        value={item.order ?? 0}
                                        onChange={e => updateMenuItem(item.id, { order: Number(e.target.value || 0) })}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {activeSection === 'home' && (
                <>
                <div className="admin-form-wrapper" style={{ marginBottom: 24 }}>
                        <h2 style={{ marginBottom: 16, fontSize: 18 }}>Hero (topo da página)</h2>
                        <div className="form-group">
                            <label>Subtítulo</label>
                            <input type="text" value={config.home?.heroSubtitle || ''} onChange={e => updateHome('heroSubtitle', e.target.value)} />
                        </div>
                    <div className="form-group">
                            <label>Título</label>
                            <input type="text" value={config.home?.heroTitle || ''} onChange={e => updateHome('heroTitle', e.target.value)} />
                    </div>
                    <div className="form-group">
                            <label>Título destaque</label>
                            <input type="text" value={config.home?.heroTitleHighlight || ''} onChange={e => updateHome('heroTitleHighlight', e.target.value)} />
                    </div>
                    <div className="form-group">
                            <label>Descrição</label>
                            <textarea rows={3} value={config.home?.heroDescription || ''} onChange={e => updateHome('heroDescription', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Imagem de fundo do hero (URL)</label>
                            <input type="text" value={config.home?.heroBackgroundImage || ''} onChange={e => updateHome('heroBackgroundImage', e.target.value)} placeholder="/assets/img/ridersoficarus.jpg" />
                        </div>
                    </div>

                    <AdminHomeCountdown />

                    <div className="admin-form-wrapper" style={{ marginBottom: 24 }}>
                        <h2 style={{ marginBottom: 16, fontSize: 18 }}>Explore the World (4 cards)</h2>
                        <p style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: 14 }}>
                            Imagens dos atalhos Guias, Montarias, Classes e Rankings na home. Deixe vazio para usar o ícone padrão.
                        </p>
                        {([
                            ['guides', 'Guias'],
                            ['mounts', 'Montarias'],
                            ['classes', 'Classes'],
                            ['rankings', 'Rankings'],
                        ] as const).map(([id, label], i) => {
                            const item = shortcuts[i] ?? shortcuts.find(s => s.id === id);
                            const index = shortcuts.findIndex(s => s.id === id);
                            const idx = index >= 0 ? index : i;
                            return (
                                <div key={id} style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--border-light)' }}>
                                    <strong>{label}</strong>
                                    <div className="form-group" style={{ marginTop: 10 }}>
                                        <label>Imagem (URL ou caminho)</label>
                                        <input
                                            type="text"
                                            value={item?.imageUrl || ''}
                                            onChange={e => updateShortcut(idx, 'imageUrl', e.target.value)}
                                            placeholder="https://... ou /uploads/..."
                                        />
                                    </div>
                                    {item?.imageUrl && (
                                        <img
                                            src={resolvePublicMediaUrl(item.imageUrl) ?? item.imageUrl}
                                            alt={label}
                                            style={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover', borderRadius: 8, marginTop: 8 }}
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="admin-form-wrapper" style={{ marginBottom: 24 }}>
                        <h2 style={{ marginBottom: 16, fontSize: 18 }}>SEO (Google / meta tags)</h2>
                        <div className="form-group">
                            <label>Título SEO</label>
                            <input type="text" value={config.home?.seoTitle || ''} onChange={e => updateHome('seoTitle', e.target.value)} placeholder="Knight Icarus Online - MMORPG" />
                        </div>
                        <div className="form-group">
                            <label>Descrição SEO</label>
                            <textarea rows={2} value={config.home?.seoDescription || ''} onChange={e => updateHome('seoDescription', e.target.value)} />
                    </div>
                    <div className="form-group">
                            <label>Palavras-chave</label>
                            <input type="text" value={config.home?.seoKeywords || ''} onChange={e => updateHome('seoKeywords', e.target.value)} placeholder="icarus, mmorpg, dragon" />
                        </div>
                </div>

                    <div className="admin-form-wrapper">
                        <h2 style={{ marginBottom: 16, fontSize: 18 }}>Game Features (8 cards)</h2>
                    {features.slice(0, 8).map((f, i) => (
                        <div key={i} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid var(--border-light)' }}>
                            <strong>Card {i + 1}</strong>
                            <div className="form-group">
                                <label>Título</label>
                                    <input type="text" value={f.title || ''} onChange={e => updateFeature(i, 'title', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label>Descrição</label>
                                    <textarea rows={2} value={f.description || ''} onChange={e => updateFeature(i, 'description', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label>Ícone (classe Remix, ex: ri-dragon-line)</label>
                                    <input type="text" value={f.icon || ''} onChange={e => updateFeature(i, 'icon', e.target.value)} placeholder="ri-dragon-line" />
                                </div>
                                <div className="form-group">
                                    <label>Imagem de fundo (URL ou caminho)</label>
                                    <input type="text" value={f.image || ''} onChange={e => updateFeature(i, 'image', e.target.value)} placeholder="/assets/images/..." />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {activeSection === 'download' && (
                <div className="admin-form-wrapper">
                    <h2 style={{ marginBottom: 16, fontSize: 18 }}>Textos da página /download</h2>
                    {([
                        ['freeBadge', 'Badge GRÁTIS/FREE'],
                        ['title', 'Título principal'],
                        ['subtitle', 'Subtítulo'],
                        ['clientDesc', 'Descrição do cliente'],
                        ['defaultButtonText', 'Texto padrão do botão (sem links)'],
                        ['systemRequirementsTitle', 'Título requisitos do sistema'],
                        ['systemRequirementsNote', 'Nota requisitos do sistema'],
                    ] as const).map(([key, label]) => (
                        <div key={key} style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--border-light)' }}>
                            <strong>{label}</strong>
                            <div className="form-group" style={{ marginTop: 10 }}>
                                <label>English</label>
                                <input
                                    type="text"
                                    value={(config.download as Record<string, { en?: string }>)?.[key]?.en || ''}
                                    onChange={e => updateDownload(key, 'en', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Português (PT-BR)</label>
                                <input
                                    type="text"
                                    value={(config.download as Record<string, { 'pt-BR'?: string }>)?.[key]?.['pt-BR'] || ''}
                                    onChange={e => updateDownload(key, 'pt-BR', e.target.value)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {activeSection === 'theme' && (
                <>
                    <div className="admin-form-wrapper admin-saved-themes" style={{ marginBottom: 24 }}>
                        <h2 style={{ marginBottom: 8, fontSize: 18 }}>Temas salvos (5 slots)</h2>
                        <p className="admin-subtitle" style={{ marginBottom: 16 }}>
                            Salve o tema atual em um dos 5 slots para recuperar depois.
                        </p>
                        <div className="saved-themes-grid">
                            {[0, 1, 2, 3, 4].map((i) => {
                                const slot = savedThemes[i];
                                const hasTheme = slot?.theme && Object.keys(slot.theme).length > 0;
                                return (
                                    <div key={i} className="saved-theme-slot">
                                        <div className="saved-theme-slot-header"><strong>Slot {i + 1}</strong></div>
                                        <input
                                            type="text"
                                            className="saved-theme-name-input"
                                            value={slot?.name ?? ''}
                                            onChange={e => setSavedThemeSlot(i, { name: e.target.value })}
                                            placeholder={`Tema ${i + 1}`}
                                        />
                                        <div className="saved-theme-slot-actions">
                                            <button type="button" className="btn btn-secondary btn-small" onClick={() => saveCurrentThemeToSlot(i)}>
                                                Salvar tema atual
                                            </button>
                                            <button type="button" className="btn btn-primary btn-small" onClick={() => applySavedTheme(i)} disabled={!hasTheme}>
                                                Aplicar tema
                                            </button>
                                        </div>
                                        {hasTheme && (
                                            <div className="saved-theme-preview">
                                                <span style={{ backgroundColor: slot.theme.goldPrimary || '#d4af37' }} />
                                                <span style={{ backgroundColor: slot.theme.dragonBlue || '#1a4d7a' }} />
                                                <span style={{ backgroundColor: slot.theme.bgDark || '#0a0a0a' }} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                </div>

                    <div className="admin-form-wrapper admin-theme-section">
                        <h2 style={{ marginBottom: 8, fontSize: 18 }}>Cores e efeitos</h2>
                    <p className="admin-subtitle" style={{ marginBottom: 16 }}>
                            Clique em um campo e depois em uma cor da paleta para aplicar.
                    </p>

                    {Array.from(new Set(THEME_FIELDS.map(f => f.group))).map(group => (
                        <div key={group} className="theme-group">
                            <h3 className="theme-group-title">{group}</h3>
                            <div className="theme-fields-grid">
                                {THEME_FIELDS.filter(f => f.group === group).map(({ key, label, type }) => {
                                    const value = config.theme?.[key] ?? '';
                                    const isSelected = selectedThemeKey === key;
                                    return (
                                        <div
                                            key={key}
                                            className={`theme-field-row ${isSelected ? 'theme-field-selected' : ''}`}
                                            onClick={() => setSelectedThemeKey(key)}
                                        >
                                            <label>{label}</label>
                                            <div className="theme-field-inputs">
                                                {type !== 'text' && (
                                                    <input
                                                        type="color"
                                                        value={value.startsWith('#') && value.length >= 7 ? value.slice(0, 7) : '#d4af37'}
                                                        onChange={e => updateTheme(key, e.target.value)}
                                                        onClick={e => e.stopPropagation()}
                                                        className="theme-color-picker"
                                                    />
                                                )}
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={e => updateTheme(key, e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                    onFocus={() => setSelectedThemeKey(key)}
                                                    placeholder={key === 'goldGlow' ? '0 0 25px rgba(212,175,55,0.2)' : '#hex'}
                                                    className="theme-hex-input"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    <div className="theme-palette-section">
                            <h3 className="theme-group-title">Paleta rápida</h3>
                        {selectedThemeKey && (
                                <p className="theme-palette-hint">
                                    Aplicando em: <strong>{THEME_FIELDS.find(f => f.key === selectedThemeKey)?.label ?? selectedThemeKey}</strong>
                                </p>
                        )}
                        <div className="theme-palette-grid">
                            {COLOR_PALETTE.map((group, gi) => (
                                <div key={gi} className="palette-group">
                                    <span className="palette-group-name">{group.name}</span>
                                    <div className="palette-swatches">
                                        {group.colors.map((hex, i) => (
                                            <button
                                                key={`${gi}-${i}`}
                                                type="button"
                                                className="palette-swatch"
                                                style={{ backgroundColor: hex }}
                                                title={hex}
                                                onClick={() => {
                                                    if (selectedThemeKey) updateTheme(selectedThemeKey, hex);
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                </>
            )}

            {activeSection === 'guildRank' && (
                <div className="guildrank-admin">
                    <div className="guildrank-fields-col">
                        <div className="admin-form-wrapper">
                            <h2 style={{ marginBottom: 8, fontSize: 18 }}>Card de guild no ranking</h2>
                            <p className="admin-subtitle" style={{ marginBottom: 16 }}>
                                Ajuste a posição (%), o tamanho e a cor de cada elemento. O preview à direita atualiza ao vivo.
                            </p>
                            <div className="guildrank-field">
                                <label className="guildrank-label">Largura máxima do card</label>
                                <div className="guildrank-slider-row">
                                    <input
                                        type="range"
                                        min={180}
                                        max={360}
                                        step={2}
                                        value={guildRank.cardMaxWidth}
                                        onChange={e => updateGuildRankTopLevel('cardMaxWidth', Number(e.target.value))}
                                    />
                                    <output>{guildRank.cardMaxWidth}px</output>
                                </div>
                            </div>
                            <label className="guildrank-check">
                                <input
                                    type="checkbox"
                                    checked={guildRank.highlightFirst}
                                    onChange={e => updateGuildRankTopLevel('highlightFirst', e.target.checked)}
                                />
                                <span>Destacar o 1º lugar (maior e elevado)</span>
                            </label>

                            <div className="guildrank-effects-group">
                                <h4 className="guildrank-effects-title">Efeitos do 1º lugar (recompensa)</h4>
                                {GUILDRANK_EFFECTS.map(fx => (
                                    <label key={fx.key} className="guildrank-check">
                                        <input
                                            type="checkbox"
                                            checked={guildRank.effects[fx.key]}
                                            onChange={e => updateGuildRankEffects(fx.key, e.target.checked)}
                                        />
                                        <span>{fx.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {GUILDRANK_ELEMENTS.map(sec => {
                            const el = guildRank[sec.key];
                            const isWidthEl = sec.key === 'emblem' || sec.key === 'placa';
                            const sizeValue = isWidthEl ? (el as { width: number }).width : (el as { fontSize: number }).fontSize;
                            return (
                                <div key={sec.key} className="admin-form-wrapper">
                                    <h3 style={{ marginBottom: 8, fontSize: 16 }}>{sec.label}</h3>
                                    <label className="guildrank-check">
                                        <input
                                            type="checkbox"
                                            checked={el.enabled}
                                            onChange={e => updateGuildRankElement(sec.key, { enabled: e.target.checked })}
                                        />
                                        <span>Visível no card</span>
                                    </label>
                                    <div className="guildrank-field">
                                        <label className="guildrank-label">Horizontal (centro %)</label>
                                        <div className="guildrank-slider-row">
                                            <input
                                                type="range"
                                                min={0}
                                                max={100}
                                                step={0.5}
                                                value={el.left}
                                                onChange={e => updateGuildRankElement(sec.key, { left: Number(e.target.value) })}
                                            />
                                            <output>{el.left}%</output>
                                        </div>
                                    </div>
                                    <div className="guildrank-field">
                                        <label className="guildrank-label">Vertical (centro %)</label>
                                        <div className="guildrank-slider-row">
                                            <input
                                                type="range"
                                                min={0}
                                                max={100}
                                                step={0.5}
                                                value={el.top}
                                                onChange={e => updateGuildRankElement(sec.key, { top: Number(e.target.value) })}
                                            />
                                            <output>{el.top}%</output>
                                        </div>
                                    </div>
                                    <div className="guildrank-field">
                                        <label className="guildrank-label">{sec.sizeLabel}</label>
                                        <div className="guildrank-slider-row">
                                            <input
                                                type="range"
                                                min={sec.sizeMin}
                                                max={sec.sizeMax}
                                                step={1}
                                                value={sizeValue}
                                                onChange={e => updateGuildRankElement(
                                                    sec.key,
                                                    isWidthEl ? { width: Number(e.target.value) } : { fontSize: Number(e.target.value) },
                                                )}
                                            />
                                            <output>{sizeValue}</output>
                                        </div>
                                    </div>
                                    {sec.hasColor && (
                                        <div className="guildrank-field">
                                            <label className="guildrank-label">Cor do texto</label>
                                            <div className="guildrank-color-row">
                                                <input
                                                    type="color"
                                                    value={(el as { color: string }).color}
                                                    onChange={e => updateGuildRankElement(sec.key, { color: e.target.value })}
                                                />
                                                <input
                                                    type="text"
                                                    className="theme-hex-input"
                                                    value={(el as { color: string }).color}
                                                    onChange={e => updateGuildRankElement(sec.key, { color: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="guildrank-preview-pane">
                        <div className="admin-form-wrapper guildrank-preview-wrap">
                            <div className="guildrank-preview-header">
                                <strong>Pré-visualização ao vivo</strong>
                                <span>Card topo do ranking</span>
                            </div>
                            <div className="guildrank-preview-stage">
                                <GuildRankingCard item={SAMPLE_GUILD} config={guildRank} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSiteConfig;
